import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { QRCode } from '../../api/models/qr.model';
import { QrCodeService, QrCodeStatistics, QrValidationResponse } from '../../api/services/qr-code.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { HeaderNavComponent } from "src/app/ui/header-nav/header-nav.component";
import { FooterComponent } from "src/app/ui/footer/footer.component";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-qr-code-generator',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatTableModule,
    MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatTabsModule, MatBadgeModule, MatSnackBarModule, MatToolbarModule,
    MatChipsModule, MatSlideToggleModule, FormsModule, ReactiveFormsModule,
    HeaderNavComponent,
    FooterComponent,
    MatProgressSpinner
],
  templateUrl: './qr-code-generator.component.html',
  styles: []
})
export class QrCodeGeneratorComponent implements OnInit {
  qrCodes$: Observable<QRCode[]>;
  newTableNumber = '';
  selectedLayout = 'single';
  isLoading = true;
  searchQuery = '';
  searchType = '';
  validateResult: QrValidationResponse | null = null;
  validateCode = '';
  mostScanned: QRCode[] = [];
  recentlyScanned: QRCode[] = [];

  private qrCodesSubject = new BehaviorSubject<QRCode[]>([]);
  private statistics: QrCodeStatistics | null = null;

  constructor(
    private qrCodeService: QrCodeService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {
    this.qrCodes$ = this.qrCodesSubject.asObservable();
  }

  ngOnInit(): void {
    this.loadQRCodes();
    this.loadStatistics();
    this.loadMostScanned();
    this.loadRecentlyScanned();
  }

  private loadMostScanned(): void {
    this.qrCodeService.getMostScanned(0, 5).subscribe({
      next: (codes) => this.mostScanned = codes,
      error: () => {}
    });
  }

  private loadRecentlyScanned(): void {
    this.qrCodeService.getRecentlyScanned(0, 5).subscribe({
      next: (codes) => this.recentlyScanned = codes,
      error: () => {}
    });
  }

  deleteQrCode(qrCode: QRCode): void {
    if (!qrCode.id) return;
    this.qrCodeService.deleteQrCode(qrCode.id).subscribe({
      next: () => {
        this.snackBar.open('QR code deleted', 'Close', { duration: 2000 });
        this.loadQRCodes();
        this.loadStatistics();
      },
      error: () => this.snackBar.open('Failed to delete QR code', 'Close', { duration: 3000 })
    });
  }

  generateTableQrCode(tableNumber: string): void {
    this.qrCodeService.generateTableQrCode(tableNumber).subscribe({
      next: () => {
        this.snackBar.open(`Table QR code generated for table ${tableNumber}`, 'Close', { duration: 3000 });
        this.loadQRCodes();
      },
      error: () => this.snackBar.open('Failed to generate table QR', 'Close', { duration: 3000 })
    });
  }

  generateMenuQrCode(): void {
    this.qrCodeService.generateMenuQrCode().subscribe({
      next: () => {
        this.snackBar.open('Menu QR code generated', 'Close', { duration: 3000 });
        this.loadQRCodes();
      },
      error: () => this.snackBar.open('Failed to generate menu QR', 'Close', { duration: 3000 })
    });
  }

  validateQrCode(): void {
    if (!this.validateCode) return;
    this.qrCodeService.validateQrCode(this.validateCode).subscribe({
      next: (result) => this.validateResult = result,
      error: () => this.snackBar.open('Validation failed', 'Close', { duration: 3000 })
    });
  }

  searchQrCodes(): void {
    this.qrCodeService.searchQrCodes({
      qrData: this.searchQuery || undefined,
      type: this.searchType || undefined,
      page: 0,
      size: 50
    }).subscribe({
      next: (page) => this.qrCodesSubject.next(page.content || []),
      error: () => this.snackBar.open('Search failed', 'Close', { duration: 3000 })
    });
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchType = '';
    this.loadQRCodes();
  }

  loadActiveQrCodes(): void {
    this.qrCodeService.getActiveQrCodes().subscribe({
      next: (codes) => this.qrCodesSubject.next(codes),
      error: () => {}
    });
  }

  loadQrByType(type: string): void {
    this.qrCodeService.getQrCodesByType(type).subscribe({
      next: (codes) => this.qrCodesSubject.next(codes),
      error: () => {}
    });
  }

  private loadQRCodes(): void {
    this.isLoading = true;
    this.qrCodeService.getQrCodes().subscribe({
      next: (codes) => {
        this.qrCodesSubject.next(codes);
      },
      error: (error) => {
        if (!environment.production) {
          console.error('Error loading QR codes:', error);
        }
        this.snackBar.open('Failed to load QR codes', 'Close', { duration: 3000 });
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  private loadStatistics(): void {
    this.isLoading = true;
    this.qrCodeService.getStatistics().subscribe({
      next: (stats) => {
        this.statistics = stats;
      },
      error: (error) => {
        if (!environment.production) {
          console.error('Error loading statistics:', error);
        }
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  onBack(): void {
    this.router.navigate(['/service-provider']);
  }

  // Statistics methods
  getTotalQRCodes(): number {
    return this.statistics?.totalQRCodes ?? this.qrCodesSubject.value.length;
  }

  getActiveQRCodes(): number {
    return this.statistics?.activeQRCodes ?? this.qrCodesSubject.value.filter(code => code.isActive).length;
  }

  getTotalScans(): number {
    return this.statistics?.totalScanCount ?? this.qrCodesSubject.value.reduce((sum, code) => sum + (code.scanCount || 0), 0);
  }

  getScannedToday(): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.qrCodesSubject.value.filter(code => {
      if (!code.lastScanned) return false;
      const scanDate = new Date(code.lastScanned);
      scanDate.setHours(0, 0, 0, 0);
      return scanDate.getTime() === today.getTime();
    }).length;
  }

  getQRCodes(): QRCode[] {
    return this.qrCodesSubject.value.sort((a, b) => {
      const aNum = parseInt(a.tableNumber?.toString() || '0');
      const bNum = parseInt(b.tableNumber?.toString() || '0');
      return aNum - bNum;
    });
  }

  // Action methods
  generateQRCode(): void {
    if (!this.newTableNumber) {
      this.snackBar.open('Please enter a table number', 'Close', { duration: 3000 });
      return;
    }

    if (this.qrCodesSubject.value.some(qr => qr.tableNumber?.toString() === this.newTableNumber)) {
      this.snackBar.open('QR code for this table already exists', 'Close', { duration: 3000 });
      return;
    }

    const request = {
      tableNumber: this.newTableNumber,
      type: 'TABLE'
    };

    this.qrCodeService.createQrCode(request).subscribe({
      next: (qrCode) => {
        this.snackBar.open(`QR code generated for Table ${this.newTableNumber}`, 'Close', { duration: 3000 });
        this.newTableNumber = '';
        this.loadQRCodes();
        this.loadStatistics();
      },
      error: (error) => {
        if (!environment.production) {
          console.error('Error creating QR code:', error);
        }
        this.snackBar.open(error.error?.message || 'Failed to create QR code', 'Close', { duration: 3000 });
      }
    });
  }

  generateBulkQRCodes(): void {
    const startTable = 1;
    const endTable = 20;

    const request = {
      startTable,
      endTable,
      type: 'TABLE'
    };

    this.qrCodeService.createBulkQrCodes(request).subscribe({
      next: (qrCodes) => {
        this.snackBar.open(`Generated ${qrCodes.length} QR codes for tables ${startTable}-${endTable}`, 'Close', { duration: 3000 });
        this.loadQRCodes();
        this.loadStatistics();
      },
      error: (error) => {
        if (!environment.production) {
          console.error('Error creating bulk QR codes:', error);
        }
        this.snackBar.open(error.error?.message || 'Failed to create bulk QR codes', 'Close', { duration: 3000 });
      }
    });
  }

  copyQRContent(qrCode: QRCode): void {
    const url = qrCode.url || qrCode.qrData || '';
    if (!url) {
      this.snackBar.open('QR code URL not available', 'Close', { duration: 3000 });
      return;
    }
    navigator.clipboard.writeText(url);
    this.snackBar.open(`Table ${qrCode.tableNumber} QR code URL copied to clipboard`, 'Close', { duration: 3000 });
  }

  /**
   * Saves the code as a PNG.
   *
   * This and printQR only opened a snackbar — they announced an action and
   * performed none. A bar could therefore never get its table codes off this
   * screen, which blocks the whole scan-to-order flow the product is built on.
   */
  downloadQR(qrCode: QRCode): void {
    const src = qrCode.qrImageUrl;
    if (!src) {
      this.snackBar.open('This code has no image to download', 'Close', { duration: 3000 });
      return;
    }
    const a = document.createElement('a');
    a.href = src;
    a.download = `qr-table-${qrCode.tableNumber || 'menu'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    this.snackBar.open(`Saved the QR code for Table ${qrCode.tableNumber}`, 'Close', { duration: 3000 });
  }

  /** Opens a print sheet with the code and its table number, sized for a table card. */
  printQR(qrCode: QRCode): void {
    const src = qrCode.qrImageUrl;
    if (!src) {
      this.snackBar.open('This code has no image to print', 'Close', { duration: 3000 });
      return;
    }
    const w = window.open('', '_blank', 'width=420,height=560');
    if (!w) {
      this.snackBar.open('Allow pop-ups to print the QR code', 'Close', { duration: 4000 });
      return;
    }
    w.document.write(`<!doctype html><html><head><title>Table ${qrCode.tableNumber}</title>
      <style>body{font-family:system-ui,sans-serif;text-align:center;padding:24px}
      img{width:300px;height:300px}h1{font-size:20px;margin:8px 0}
      p{color:#555;font-size:12px;word-break:break-all}</style></head>
      <body onload="window.print()">
        <h1>Table ${qrCode.tableNumber ?? ''}</h1>
        <img src="${src}" alt="QR code">
        <p>${qrCode.qrData ?? ''}</p>
      </body></html>`);
    w.document.close();
    this.snackBar.open(`Printing the QR code for Table ${qrCode.tableNumber}`, 'Close', { duration: 3000 });
  }

  toggleQRStatus(qrCode: QRCode): void {
    if (!qrCode.id) {
      this.snackBar.open('Cannot update QR code: missing ID', 'Close', { duration: 3000 });
      return;
    }

    const newStatus = !qrCode.isActive;

    this.qrCodeService.updateQrCodeStatus(qrCode, newStatus).subscribe({
      next: (updatedQRCode) => {
        this.snackBar.open(`QR code ${newStatus ? 'activated' : 'deactivated'}`, 'Close', { duration: 2000 });
        this.loadQRCodes();
        this.loadStatistics();
      },
      error: (error) => {
        if (!environment.production) {
          console.error('Error updating QR code status:', error);
        }
        this.snackBar.open('Failed to update QR code status', 'Close', { duration: 3000 });
      }
    });
  }
}